import React, { useState, useEffect, useRef } from 'react';
import { User, Link, Share2, Eye, Save, Plus, X, Copy, Check, Download, QrCode, Lock, Zap, Upload, Phone, Camera, Star } from 'lucide-react';
import { 
  FaFacebook, FaTwitter, FaInstagram, FaLinkedin, FaYoutube, FaWhatsapp, 
  FaTelegram, FaSnapchat, FaPinterest, FaTiktok, FaDiscord, FaReddit,
  FaGlobe, FaGithub, FaSpotify, FaThreads
} from 'react-icons/fa6';
import { QRCodeSVG } from 'qrcode.react';
import html2canvas from 'html2canvas';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { copyToClipboard } from '../../utils/clipboard';
import { getImageUrl } from '../../utils/imageUrl';
import Cosmic4KDVCTemplate from '../../components/Cosmic4KDVCTemplate';

// METAVERSE-STYLE PREMIUM TEMPLATES
const TEMPLATES = [
  // ===== 4K DVC COSMIC TEMPLATES =====
  { value: 'cosmic-4k-galaxy', label: '4K Cosmic Galaxy', color: '#00BFFF', bg: '#000820', style: '4k-cosmic', 
    gradient: 'from-blue-600 via-purple-600 to-pink-500', glow: 'cyan', animation: 'cosmic',
    preview: 'https://customer-assets.emergentagent.com/job_276ae801-dbfc-4047-88b6-a018146ee204/artifacts/g2cdpyr2_Show_your_creativity_202602241042.jpeg',
    features: ['floating-social-icons', 'holographic-frame', 'space-background', '4k-badge'] },
  { value: 'cosmic-4k-nebula', label: '4K Cosmic Nebula', color: '#FF6EC7', bg: '#0a0014', style: '4k-cosmic',
    gradient: 'from-pink-500 via-purple-500 to-blue-600', glow: 'pink', animation: 'cosmic',
    features: ['floating-social-icons', 'holographic-frame', 'nebula-background', '4k-badge'] },
  { value: 'cosmic-4k-aurora', label: '4K Cosmic Aurora', color: '#00FF88', bg: '#000a0a', style: '4k-cosmic',
    gradient: 'from-green-400 via-cyan-500 to-purple-600', glow: 'green', animation: 'cosmic',
    features: ['floating-social-icons', 'holographic-frame', 'aurora-background', '4k-badge'] },
  { value: 'cosmic-4k-sunrise', label: '4K Cosmic Sunrise', color: '#FF8C00', bg: '#1a0500', style: '4k-cosmic',
    gradient: 'from-orange-500 via-red-500 to-purple-600', glow: 'orange', animation: 'cosmic',
    features: ['floating-social-icons', 'holographic-frame', 'sunrise-background', '4k-badge'] },
  { value: 'cosmic-4k-midnight', label: '4K Cosmic Midnight', color: '#8B5CF6', bg: '#05001a', style: '4k-cosmic',
    gradient: 'from-violet-600 via-indigo-600 to-blue-600', glow: 'purple', animation: 'cosmic',
    features: ['floating-social-icons', 'holographic-frame', 'midnight-background', '4k-badge'] },
    
  // ===== SARAH JENSEN STYLE - Glass Morphism Business =====
  { value: 'business-navy-gold', label: 'Business Navy Gold', color: '#FFD700', bg: '#0a1628', style: 'business-glass',
    gradient: 'from-blue-900 via-slate-800 to-blue-950', glow: 'gold', animation: 'shimmer' },
  { value: 'business-executive', label: 'Executive Suite', color: '#D4AF37', bg: '#0d1520', style: 'business-glass',
    gradient: 'from-slate-800 via-gray-900 to-slate-950', glow: 'gold', animation: 'shimmer' },
  { value: 'business-modern', label: 'Modern Executive', color: '#F59E0B', bg: '#111827', style: 'business-glass',
    gradient: 'from-gray-800 via-slate-800 to-gray-900', glow: 'amber', animation: 'none' },
  { value: 'corporate-blue', label: 'Corporate Blue', color: '#3B82F6', bg: '#0c1929', style: 'business-glass',
    gradient: 'from-blue-900 via-indigo-900 to-slate-900', glow: 'blue', animation: 'pulse' },
  { value: 'corporate-teal', label: 'Corporate Teal', color: '#14B8A6', bg: '#0d1f1f', style: 'business-glass',
    gradient: 'from-teal-900 via-slate-900 to-teal-950', glow: 'teal', animation: 'none' },
    
  // ===== ALEX RIVERS STYLE - Neon Ring Profile =====
  { value: 'neon-ring-cyan', label: 'Neon Ring Cyan', color: '#00FFFF', bg: '#0a0a1a', style: 'neon-ring',
    gradient: 'from-cyan-500 via-blue-500 to-purple-600', glow: 'cyan', animation: 'pulse' },
  { value: 'neon-ring-purple', label: 'Neon Ring Purple', color: '#A855F7', bg: '#0d0020', style: 'neon-ring',
    gradient: 'from-purple-500 via-pink-500 to-blue-500', glow: 'purple', animation: 'pulse' },
  { value: 'neon-ring-green', label: 'Neon Ring Matrix', color: '#22C55E', bg: '#0a120a', style: 'neon-ring',
    gradient: 'from-green-500 via-emerald-500 to-cyan-500', glow: 'green', animation: 'pulse' },
  { value: 'neon-ring-pink', label: 'Neon Ring Pink', color: '#EC4899', bg: '#1a0a18', style: 'neon-ring',
    gradient: 'from-pink-500 via-rose-500 to-purple-500', glow: 'pink', animation: 'pulse' },
  { value: 'neon-ring-orange', label: 'Neon Ring Fire', color: '#F97316', bg: '#1a0f0a', style: 'neon-ring',
    gradient: 'from-orange-500 via-red-500 to-pink-500', glow: 'orange', animation: 'pulse' },
    
  // ===== MAYA SHARMA STYLE - Creative Studio =====
  { value: 'creative-studio-purple', label: 'Creative Studio', color: '#8B5CF6', bg: '#0f0a2a', style: 'creative-studio',
    gradient: 'from-violet-600 via-purple-700 to-blue-800', glow: 'purple', animation: 'gradient' },
  { value: 'creative-vision-blue', label: 'Creative Vision', color: '#06B6D4', bg: '#0a1520', style: 'creative-studio',
    gradient: 'from-cyan-600 via-blue-700 to-purple-700', glow: 'cyan', animation: 'gradient' },
  { value: 'designer-pro', label: 'Designer Pro', color: '#F472B6', bg: '#1a0a20', style: 'creative-studio',
    gradient: 'from-pink-600 via-purple-600 to-indigo-700', glow: 'pink', animation: 'gradient' },
  { value: 'artist-palette', label: 'Artist Palette', color: '#FBBF24', bg: '#1a150a', style: 'creative-studio',
    gradient: 'from-amber-500 via-orange-600 to-red-600', glow: 'amber', animation: 'shimmer' },
  { value: 'tech-innovator', label: 'Tech Innovator', color: '#10B981', bg: '#0a1a15', style: 'creative-studio',
    gradient: 'from-emerald-600 via-teal-600 to-cyan-600', glow: 'green', animation: 'pulse' },
    
  // ===== SPLIT LAYOUT BUSINESS =====
  { value: 'split-dark-purple', label: 'Split Dark Purple', color: '#A78BFA', bg: '#0f0f1a', style: 'split-layout',
    gradient: 'from-violet-900 via-purple-900 to-slate-900', glow: 'purple', animation: 'none' },
  { value: 'split-professional', label: 'Split Professional', color: '#60A5FA', bg: '#0a1020', style: 'split-layout',
    gradient: 'from-blue-900 via-slate-900 to-gray-900', glow: 'blue', animation: 'none' },
  { value: 'split-elegant', label: 'Split Elegant', color: '#F9A8D4', bg: '#1a0a15', style: 'split-layout',
    gradient: 'from-pink-900 via-slate-900 to-purple-950', glow: 'pink', animation: 'shimmer' },
  { value: 'split-tech', label: 'Split Tech', color: '#34D399', bg: '#0a1a18', style: 'split-layout',
    gradient: 'from-emerald-900 via-slate-900 to-teal-950', glow: 'green', animation: 'pulse' },
  { value: 'split-minimal', label: 'Split Minimal', color: '#E5E7EB', bg: '#111111', style: 'split-layout',
    gradient: 'from-gray-900 via-slate-900 to-zinc-900', glow: 'white', animation: 'none' },
    
  // ===== 4K QUALITY TEMPLATES =====
  { value: '4k-tech-blue', label: '4K Tech Blue', color: '#38BDF8', bg: '#0c1929', style: '4k-quality',
    gradient: 'from-sky-600 via-blue-700 to-indigo-800', glow: 'cyan', animation: 'pulse' },
  { value: '4k-tech-green', label: '4K Tech Green', color: '#4ADE80', bg: '#0a1a12', style: '4k-quality',
    gradient: 'from-green-600 via-emerald-700 to-teal-800', glow: 'green', animation: 'pulse' },
  { value: '4k-premium-gold', label: '4K Premium Gold', color: '#FCD34D', bg: '#1a1508', style: '4k-quality',
    gradient: 'from-amber-600 via-yellow-600 to-orange-700', glow: 'gold', animation: 'shimmer' },
  { value: '4k-elegant-rose', label: '4K Elegant Rose', color: '#FB7185', bg: '#1a0a10', style: '4k-quality',
    gradient: 'from-rose-600 via-pink-700 to-purple-800', glow: 'pink', animation: 'shimmer' },
  { value: '4k-ocean-deep', label: '4K Ocean Deep', color: '#22D3EE', bg: '#0a1520', style: '4k-quality',
    gradient: 'from-cyan-600 via-blue-700 to-indigo-900', glow: 'cyan', animation: 'wave' },
    
  // ===== SOCIAL GRID TEMPLATES =====
  { value: 'social-grid-dark', label: 'Social Grid Dark', color: '#818CF8', bg: '#0a0a15', style: 'social-grid',
    gradient: 'from-indigo-900 via-purple-900 to-slate-900', glow: 'purple', animation: 'none' },
  { value: 'social-grid-neon', label: 'Social Grid Neon', color: '#22D3EE', bg: '#0a1018', style: 'social-grid',
    gradient: 'from-cyan-900 via-blue-900 to-purple-900', glow: 'cyan', animation: 'pulse' },
  { value: 'social-grid-gradient', label: 'Social Grid Gradient', color: '#F472B6', bg: '#0f0a18', style: 'social-grid',
    gradient: 'from-pink-900 via-purple-900 to-indigo-900', glow: 'pink', animation: 'gradient' },
  { value: 'social-grid-minimal', label: 'Social Grid Minimal', color: '#94A3B8', bg: '#0f0f0f', style: 'social-grid',
    gradient: 'from-slate-900 via-gray-900 to-zinc-900', glow: 'white', animation: 'none' },
  { value: 'social-grid-vibrant', label: 'Social Grid Vibrant', color: '#A3E635', bg: '#0a1508', style: 'social-grid',
    gradient: 'from-lime-900 via-green-900 to-emerald-900', glow: 'green', animation: 'pulse' },
    
  // ===== CYBERCITY METAVERSE TEMPLATES =====
  { value: 'cybercity-neon', label: 'Cybercity Neon', color: '#00FFFF', bg: '#0a0a1a', style: 'cybercity', 
    gradient: 'from-cyan-500 via-purple-500 to-pink-500', glow: 'cyan', animation: 'pulse' },
  { value: 'cybercity-purple', label: 'Cybercity Purple', color: '#A855F7', bg: '#0d0020', style: 'cybercity',
    gradient: 'from-purple-600 via-blue-600 to-cyan-500', glow: 'purple', animation: 'pulse' },
  { value: 'cybercity-rainbow', label: 'Cybercity Rainbow', color: '#FF6EC7', bg: '#0a0014', style: 'cybercity',
    gradient: 'from-pink-500 via-orange-500 to-green-500', glow: 'pink', animation: 'gradient' },
  { value: 'cybercity-green', label: 'Cybercity Matrix', color: '#00FF88', bg: '#000a0a', style: 'cybercity',
    gradient: 'from-green-500 via-cyan-500 to-blue-500', glow: 'green', animation: 'pulse' },
  { value: 'cybercity-gold', label: 'Cybercity Gold', color: '#FFD700', bg: '#0a0808', style: 'cybercity',
    gradient: 'from-amber-500 via-orange-500 to-red-500', glow: 'gold', animation: 'shimmer' },
    
  // ===== HYPER FUTURISTIC TEMPLATES =====
  { value: 'metaverse-neon', label: 'Metaverse Neon', color: '#00FFFF', bg: '#0a0014', style: 'metaverse', 
    gradient: 'from-violet-600 via-fuchsia-600 to-cyan-500', glow: 'cyan', animation: 'pulse' },
  { value: 'cyber-punk', label: 'Cyber Punk', color: '#FF00FF', bg: '#0d0015', style: 'cyberpunk',
    gradient: 'from-pink-600 via-purple-600 to-blue-600', glow: 'pink', animation: 'glitch' },
  { value: 'hologram', label: 'Hologram 3D', color: '#00FF88', bg: '#000a0a', style: 'hologram',
    gradient: 'from-emerald-400 via-cyan-500 to-blue-500', glow: 'green', animation: 'float' },
  { value: 'matrix-code', label: 'Matrix Code', color: '#00FF00', bg: '#000800', style: 'matrix',
    gradient: 'from-green-500 to-emerald-700', glow: 'green', animation: 'rain' },
  { value: 'quantum', label: 'Quantum Flux', color: '#8B5CF6', bg: '#0a0020', style: 'quantum',
    gradient: 'from-violet-500 via-purple-500 to-pink-500', glow: 'purple', animation: 'wave' },
  { value: 'neon-grid', label: 'Neon Grid', color: '#FF3366', bg: '#080818', style: 'metaverse',
    gradient: 'from-rose-500 via-pink-500 to-violet-500', glow: 'pink', animation: 'pulse' },
  { value: 'synthwave', label: 'Synthwave', color: '#FF6EC7', bg: '#1a0a2e', style: 'metaverse',
    gradient: 'from-purple-500 via-pink-400 to-orange-400', glow: 'pink', animation: 'gradient' },
  { value: 'virtual-reality', label: 'Virtual Reality', color: '#00E5FF', bg: '#001428', style: 'metaverse',
    gradient: 'from-blue-400 via-cyan-400 to-teal-400', glow: 'cyan', animation: 'pulse' },
  
  // ===== PREMIUM LUXURY TEMPLATES =====  
  { value: 'gold-luxury', label: 'Gold Luxury', color: '#FFD700', bg: '#1a1000', style: 'luxury',
    gradient: 'from-amber-500 via-yellow-400 to-amber-600', glow: 'gold', animation: 'shimmer' },
  { value: 'diamond', label: 'Diamond Elite', color: '#B9F2FF', bg: '#0a1520', style: 'diamond',
    gradient: 'from-cyan-200 via-white to-cyan-300', glow: 'white', animation: 'sparkle' },
  { value: 'carbon-fiber', label: 'Carbon Fiber', color: '#FFFFFF', bg: '#0a0a0a', style: 'carbon',
    gradient: 'from-gray-800 via-gray-900 to-black', glow: 'white', animation: 'none' },
  { value: 'platinum', label: 'Platinum Premium', color: '#E5E4E2', bg: '#15151A', style: 'luxury',
    gradient: 'from-gray-300 via-white to-gray-400', glow: 'white', animation: 'shimmer' },
  { value: 'rose-gold', label: 'Rose Gold', color: '#E8B4B8', bg: '#1a1215', style: 'luxury',
    gradient: 'from-rose-300 via-pink-200 to-amber-200', glow: 'pink', animation: 'shimmer' },
  { value: 'emerald', label: 'Emerald Royal', color: '#50C878', bg: '#0a1a10', style: 'luxury',
    gradient: 'from-emerald-400 via-green-500 to-teal-600', glow: 'green', animation: 'none' },
  { value: 'sapphire', label: 'Sapphire Blue', color: '#0F52BA', bg: '#0a0a20', style: 'luxury',
    gradient: 'from-blue-500 via-indigo-600 to-violet-700', glow: 'blue', animation: 'shimmer' },
  { value: 'ruby', label: 'Ruby Red', color: '#E0115F', bg: '#1a0a0a', style: 'luxury',
    gradient: 'from-red-500 via-rose-600 to-pink-700', glow: 'red', animation: 'shimmer' },
  { value: 'obsidian', label: 'Obsidian Black', color: '#36454F', bg: '#050505', style: 'luxury',
    gradient: 'from-gray-900 via-slate-900 to-black', glow: 'gray', animation: 'none' },
  
  // ===== NATURE & COSMIC TEMPLATES =====
  { value: 'aurora', label: 'Aurora Borealis', color: '#7CFF7C', bg: '#001020', style: 'aurora',
    gradient: 'from-green-400 via-cyan-500 to-purple-600', glow: 'cyan', animation: 'aurora' },
  { value: 'galaxy', label: 'Galaxy Stars', color: '#E0B0FF', bg: '#050510', style: 'galaxy',
    gradient: 'from-purple-600 via-pink-500 to-orange-400', glow: 'purple', animation: 'twinkle' },
  { value: 'sunset-vibes', label: 'Sunset Vibes', color: '#FF6B6B', bg: '#1a0a10', style: 'sunset',
    gradient: 'from-orange-500 via-rose-500 to-purple-600', glow: 'orange', animation: 'gradient' },
  { value: 'nebula', label: 'Cosmic Nebula', color: '#C084FC', bg: '#0D0418', style: 'galaxy',
    gradient: 'from-fuchsia-500 via-purple-600 to-blue-700', glow: 'purple', animation: 'twinkle' },
  { value: 'northern-lights', label: 'Northern Lights', color: '#4ADE80', bg: '#061010', style: 'aurora',
    gradient: 'from-teal-400 via-emerald-400 to-cyan-500', glow: 'green', animation: 'aurora' },
  { value: 'fire-storm', label: 'Fire Storm', color: '#FF4500', bg: '#1A0800', style: 'sunset',
    gradient: 'from-red-500 via-orange-500 to-amber-500', glow: 'orange', animation: 'gradient' },
  { value: 'ocean-wave', label: 'Ocean Wave', color: '#0EA5E9', bg: '#0a1520', style: 'ocean',
    gradient: 'from-blue-600 via-cyan-600 to-teal-600', glow: 'cyan', animation: 'wave' },
  { value: 'forest-mist', label: 'Forest Mist', color: '#22C55E', bg: '#0a1a0a', style: 'aurora',
    gradient: 'from-green-600 via-emerald-600 to-teal-600', glow: 'green', animation: 'none' },
  { value: 'starfield', label: 'Starfield', color: '#FBBF24', bg: '#050510', style: 'galaxy',
    gradient: 'from-amber-500 via-yellow-400 to-orange-500', glow: 'gold', animation: 'twinkle' },
  
  // ===== CLASSIC PREMIUM TEMPLATES =====
  { value: 'default', label: 'Classic Gold', color: '#D4AF37', bg: '#0a0a0a', style: 'classic',
    gradient: 'from-amber-600 to-yellow-500', glow: 'gold', animation: 'none' },
  { value: 'tech-cyan', label: 'Tech Cyan', color: '#00D9FF', bg: '#0a192f', style: 'tech',
    gradient: 'from-cyan-500 to-blue-600', glow: 'cyan', animation: 'pulse' },
  { value: 'ocean', label: 'Deep Ocean', color: '#06B6D4', bg: '#0c4a6e', style: 'ocean',
    gradient: 'from-blue-600 via-cyan-600 to-teal-500', glow: 'blue', animation: 'wave' },
  { value: 'royal', label: 'Royal Purple', color: '#A855F7', bg: '#1c1017', style: 'royal',
    gradient: 'from-purple-600 via-violet-600 to-indigo-600', glow: 'purple', animation: 'none' },
  { value: 'midnight', label: 'Midnight Blue', color: '#3B82F6', bg: '#0A1628', style: 'classic',
    gradient: 'from-blue-500 via-indigo-500 to-violet-500', glow: 'blue', animation: 'none' },
  { value: 'forest', label: 'Forest Green', color: '#22C55E', bg: '#0A1A0A', style: 'classic',
    gradient: 'from-green-500 via-emerald-500 to-teal-500', glow: 'green', animation: 'none' },
  
  // ===== LEGACY TEMPLATES =====
  { value: 'tech-gold', label: 'Tech Gold', color: '#D4AF37', bg: '#0a0a0a', style: 'luxury',
    gradient: 'from-amber-500 via-yellow-500 to-amber-600', glow: 'gold', animation: 'shimmer' },
  { value: 'modern', label: 'Modern', color: '#3B82F6', bg: '#0a192f', style: 'tech',
    gradient: 'from-blue-500 to-indigo-600', glow: 'blue', animation: 'pulse' },
  { value: 'elegant', label: 'Elegant', color: '#A855F7', bg: '#1a1025', style: 'luxury',
    gradient: 'from-purple-500 to-pink-500', glow: 'purple', animation: 'shimmer' },
  { value: 'minimal', label: 'Minimal', color: '#FFFFFF', bg: '#111111', style: 'classic',
    gradient: 'from-gray-400 to-gray-600', glow: 'white', animation: 'none' },
  { value: 'gradient', label: 'Gradient', color: '#EC4899', bg: '#1a0a20', style: 'metaverse',
    gradient: 'from-pink-500 via-purple-500 to-blue-500', glow: 'pink', animation: 'gradient' },
];

const SOCIAL_PLATFORMS = [
  { value: 'whatsapp', label: 'WhatsApp', icon: FaWhatsapp, color: '#25D366' },
  { value: 'instagram', label: 'Instagram', icon: FaInstagram, color: '#E4405F' },
  { value: 'facebook', label: 'Facebook', icon: FaFacebook, color: '#1877F2' },
  { value: 'youtube', label: 'YouTube', icon: FaYoutube, color: '#FF0000' },
  { value: 'twitter', label: 'Twitter/X', icon: FaTwitter, color: '#1DA1F2' },
  { value: 'linkedin', label: 'LinkedIn', icon: FaLinkedin, color: '#0A66C2' },
  { value: 'telegram', label: 'Telegram', icon: FaTelegram, color: '#0088cc' },
  { value: 'snapchat', label: 'Snapchat', icon: FaSnapchat, color: '#FFFC00' },
  { value: 'tiktok', label: 'TikTok', icon: FaTiktok, color: '#000000' },
  { value: 'pinterest', label: 'Pinterest', icon: FaPinterest, color: '#E60023' },
  { value: 'discord', label: 'Discord', icon: FaDiscord, color: '#5865F2' },
  { value: 'reddit', label: 'Reddit', icon: FaReddit, color: '#FF4500' },
  { value: 'threads', label: 'Threads', icon: FaThreads, color: '#000000' },
  { value: 'spotify', label: 'Spotify', icon: FaSpotify, color: '#1DB954' },
  { value: 'github', label: 'GitHub', icon: FaGithub, color: '#181717' },
  { value: 'website', label: 'Website', icon: FaGlobe, color: '#6366F1' },
];

const DigitalCard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const cardRef = useRef(null);
  const profileInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingProfile, setUploadingProfile] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [stats, setStats] = useState({ views: 0 });
  const [isActivated, setIsActivated] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [formData, setFormData] = useState({
    template: 'default',
    display_name: '',
    tagline: '',
    bio: '',
    profile_image: '',
    cover_image: '',
    mobile_number: '',
    show_email: true,
    show_phone: true,
    show_mobile: true,
    show_whatsapp: false,
    whatsapp_number: '',
    social_links: [],
    custom_links: [],
    primary_color: '#D4AF37',
    background_color: '#0a0a0a'
  });
  const [newSocial, setNewSocial] = useState({ platform: 'whatsapp', url: '' });
  const [newLink, setNewLink] = useState({ name: '', url: '' });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    checkActivationAndFetch();
  }, []);

  const checkActivationAndFetch = async () => {
    try {
      // Check activation status
      const statusRes = await api.get('/activation/status');
      const activated = statusRes.data?.is_activated || false;
      setIsActivated(activated);
      
      if (activated) {
        await fetchCard();
        await fetchStats();
      }
    } catch (error) {
      console.error('Failed to check activation:', error);
      if (error.response?.status === 403) {
        setIsActivated(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCard = async () => {
    try {
      const res = await api.get('/dvc/my-card');
      if (res.data) {
        setCard(res.data);
        setFormData({
          template: res.data.template || 'default',
          display_name: res.data.display_name || '',
          tagline: res.data.tagline || '',
          bio: res.data.bio || '',
          profile_image: res.data.profile_image || '',
          cover_image: res.data.cover_image || '',
          mobile_number: res.data.mobile_number || '',
          show_email: res.data.show_email !== false,
          show_phone: res.data.show_phone !== false,
          show_mobile: res.data.show_mobile !== false,
          show_whatsapp: res.data.show_whatsapp || false,
          whatsapp_number: res.data.whatsapp_number || '',
          social_links: res.data.social_links || [],
          custom_links: res.data.custom_links || [],
          primary_color: res.data.primary_color || '#D4AF37',
          background_color: res.data.background_color || '#0a0a0a'
        });
      } else {
        setFormData(prev => ({ 
          ...prev, 
          display_name: user?.name || user?.username || '',
          mobile_number: user?.whatsapp_number || ''
        }));
      }
    } catch (error) {
      if (error.response?.status !== 403) {
        console.error('Failed to fetch card:', error);
      }
    }
  };

  const fetchStats = async () => {
    try {
      const res = await api.get('/dvc/stats');
      setStats(res.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.display_name) {
      toast.error('Display name is required');
      return;
    }

    setSaving(true);
    try {
      if (card) {
        await api.put('/dvc/update', formData);
        toast.success('Card updated!');
      } else {
        await api.post('/dvc/create', formData);
        toast.success('Card created!');
      }
      fetchCard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save card');
    } finally {
      setSaving(false);
    }
  };

  const normalizeUrl = (url) => {
    if (!url) return url;
    url = url.trim();
    // Add https:// if no protocol specified
    if (!url.match(/^https?:\/\//i) && !url.startsWith('tel:') && !url.startsWith('mailto:')) {
      return 'https://' + url;
    }
    return url;
  };

  const handleTemplateChange = (template) => {
    const t = TEMPLATES.find(t => t.value === template);
    setFormData({ 
      ...formData, 
      template, 
      primary_color: t?.color || '#D4AF37',
      background_color: t?.bg || '#0a0a0a'
    });
  };

  const addSocialLink = () => {
    if (newSocial.url.trim()) {
      const normalizedUrl = normalizeUrl(newSocial.url);
      setFormData({
        ...formData,
        social_links: [...formData.social_links, { ...newSocial, url: normalizedUrl }]
      });
      setNewSocial({ platform: 'whatsapp', url: '' });
    }
  };

  const removeSocialLink = (index) => {
    setFormData({
      ...formData,
      social_links: formData.social_links.filter((_, i) => i !== index)
    });
  };

  const addCustomLink = () => {
    if (newLink.name.trim() && newLink.url.trim()) {
      const normalizedUrl = normalizeUrl(newLink.url);
      setFormData({
        ...formData,
        custom_links: [...formData.custom_links, { ...newLink, url: normalizedUrl }]
      });
      setNewLink({ name: '', url: '' });
    }
  };

  const removeCustomLink = (index) => {
    setFormData({
      ...formData,
      custom_links: formData.custom_links.filter((_, i) => i !== index)
    });
  };

  const copyCardLink = async () => {
    const cardUrl = `${window.location.origin}/card/${user?.username}`;
    const success = await copyToClipboard(cardUrl);
    if (success) {
      setCopied(true);
      toast.success('Card link copied!');
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error('Failed to copy');
    }
  };

  const downloadCard = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: formData.background_color,
        scale: 2,
        useCORS: true
      });
      const link = document.createElement('a');
      link.download = `${user?.username || 'my'}-card.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Card downloaded!');
    } catch (error) {
      toast.error('Failed to download card');
    } finally {
      setDownloading(false);
    }
  };

  const getSocialIcon = (platform) => {
    const social = SOCIAL_PLATFORMS.find(s => s.value === platform);
    return social ? social.icon : FaGlobe;
  };

  const getSocialColor = (platform) => {
    const social = SOCIAL_PLATFORMS.find(s => s.value === platform);
    return social?.color || formData.primary_color;
  };

  // Handle image upload from device
  const handleImageUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image (JPG, PNG, GIF, WEBP)');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    const setUploading = type === 'profile' ? setUploadingProfile : setUploadingCover;
    setUploading(true);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const res = await api.post('/upload/image', formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (res.data?.url) {
        const fieldName = type === 'profile' ? 'profile_image' : 'cover_image';
        setFormData(prev => ({ ...prev, [fieldName]: res.data.url }));
        toast.success(`${type === 'profile' ? 'Profile' : 'Cover'} image uploaded!`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  // Show activation required message
  if (!isActivated) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6" data-testid="dvc-activation-required">
        <div className="max-w-2xl mx-auto">
          <div className="glass-card p-8 text-center border-2 border-yellow-500/30">
            <Lock className="w-20 h-20 text-yellow-500 mx-auto mb-6" />
            <h1 className="text-3xl font-bold mb-4">Activation Required</h1>
            <p className="text-muted-foreground mb-6">
              The Digital Visiting Card (DVC) feature is exclusively available for activated members.
              Please purchase and activate your account to unlock this feature.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/activation')}
                className="btn-primary flex items-center justify-center gap-2"
              >
                <Zap className="w-5 h-5" />
                Get Activated Now
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-6 py-3 bg-white/10 rounded-xl hover:bg-white/20"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const cardUrl = `${window.location.origin}/card/${user?.username}`;

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="digital-card-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <h1 className="text-4xl font-bold flex items-center gap-3">
            <User className="w-10 h-10 text-primary" />
            Digital Visiting Card
          </h1>
          {card && (
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Eye className="w-5 h-5" />
                <span>{stats.views} views</span>
              </div>
              <button onClick={() => setShowQR(true)} className="btn-secondary flex items-center gap-2">
                <QrCode className="w-5 h-5" />
                QR Code
              </button>
              <button onClick={downloadCard} disabled={downloading} className="btn-secondary flex items-center gap-2">
                <Download className="w-5 h-5" />
                {downloading ? 'Saving...' : 'Download'}
              </button>
              <button onClick={copyCardLink} className="btn-primary flex items-center gap-2">
                {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                {copied ? 'Copied!' : 'Copy Link'}
              </button>
            </div>
          )}
        </div>

        {/* QR Code Modal */}
        {showQR && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowQR(false)}>
            <div className="bg-[#1a1a1a] rounded-2xl p-8 text-center" onClick={e => e.stopPropagation()}>
              <h3 className="text-xl font-bold mb-4">Scan to View Card</h3>
              <div className="bg-white p-4 rounded-xl inline-block mb-4">
                <QRCodeSVG value={cardUrl} size={200} level="H" />
              </div>
              <p className="text-sm text-muted-foreground mb-4">{cardUrl}</p>
              <button onClick={() => setShowQR(false)} className="btn-primary">Close</button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Form */}
          <div className="glass-card p-6 max-h-[80vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-6">Edit Your Card</h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Template Selection */}
              <div>
                <label className="block text-sm mb-2">Template</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {TEMPLATES.map((t) => (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => handleTemplateChange(t.value)}
                      className={`p-3 rounded-xl border-2 text-center text-xs ${
                        formData.template === t.value
                          ? 'border-primary bg-primary/20'
                          : 'border-white/10 hover:border-white/30'
                      }`}
                      style={{ borderColor: formData.template === t.value ? t.color : undefined }}
                    >
                      <div className="w-4 h-4 rounded-full mx-auto mb-1" style={{ backgroundColor: t.color }} />
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Basic Info */}
              <div>
                <label className="block text-sm mb-1">Display Name *</label>
                <input
                  type="text"
                  value={formData.display_name}
                  onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                  placeholder="Your Name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm mb-1">Tagline</label>
                <input
                  type="text"
                  value={formData.tagline}
                  onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                  placeholder="e.g. Digital Entrepreneur | Affiliate Network Leader"
                />
              </div>

              <div>
                <label className="block text-sm mb-1">Bio</label>
                <textarea
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                  rows={2}
                  placeholder="Tell people about yourself..."
                />
              </div>

              {/* Images */}
              <div className="space-y-4">
                {/* Profile Image */}
                <div className="p-4 bg-white/5 rounded-xl">
                  <label className="block text-sm font-medium mb-2">Profile Image</label>
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-20 h-20 rounded-full border-2 border-dashed border-white/20 flex items-center justify-center overflow-hidden bg-white/5 cursor-pointer hover:border-primary/50 transition-all"
                      onClick={() => profileInputRef.current?.click()}
                    >
                      {formData.profile_image ? (
                        <img src={getImageUrl(formData.profile_image)} alt="Profile" className="w-full h-full object-cover" />
                      ) : uploadingProfile ? (
                        <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
                      ) : (
                        <Camera className="w-8 h-8 text-gray-500" />
                      )}
                    </div>
                    <div className="flex-1">
                      <input
                        ref={profileInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={(e) => handleImageUpload(e, 'profile')}
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => profileInputRef.current?.click()}
                        disabled={uploadingProfile}
                        className="px-4 py-2 bg-primary/20 text-primary rounded-lg text-sm flex items-center gap-2 hover:bg-primary/30 transition-all mb-2"
                      >
                        <Upload className="w-4 h-4" />
                        {uploadingProfile ? 'Uploading...' : 'Upload from Device'}
                      </button>
                      <input
                        type="url"
                        value={formData.profile_image}
                        onChange={(e) => setFormData({ ...formData, profile_image: e.target.value })}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs"
                        placeholder="Or paste image URL..."
                      />
                    </div>
                  </div>
                </div>

                {/* Cover Image */}
                <div className="p-4 bg-white/5 rounded-xl">
                  <label className="block text-sm font-medium mb-2">Cover Image</label>
                  <div 
                    className="h-24 rounded-lg border-2 border-dashed border-white/20 flex items-center justify-center overflow-hidden bg-white/5 cursor-pointer hover:border-primary/50 transition-all mb-2"
                    onClick={() => coverInputRef.current?.click()}
                    style={{
                      backgroundImage: formData.cover_image ? `url(${getImageUrl(formData.cover_image)})` : undefined,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  >
                    {!formData.cover_image && (
                      uploadingCover ? (
                        <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
                      ) : (
                        <div className="text-center">
                          <Upload className="w-6 h-6 text-gray-500 mx-auto mb-1" />
                          <span className="text-xs text-gray-500">Click to upload cover</span>
                        </div>
                      )
                    )}
                  </div>
                  <input
                    ref={coverInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => handleImageUpload(e, 'cover')}
                    className="hidden"
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => coverInputRef.current?.click()}
                      disabled={uploadingCover}
                      className="px-3 py-2 bg-primary/20 text-primary rounded-lg text-xs flex items-center gap-1 hover:bg-primary/30"
                    >
                      <Upload className="w-3 h-3" />
                      {uploadingCover ? 'Uploading...' : 'Upload'}
                    </button>
                    <input
                      type="url"
                      value={formData.cover_image}
                      onChange={(e) => setFormData({ ...formData, cover_image: e.target.value })}
                      className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs"
                      placeholder="Or paste cover image URL..."
                    />
                  </div>
                </div>
              </div>

              {/* Mobile Number */}
              <div className="p-4 bg-white/5 rounded-xl">
                <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                  <Phone className="w-4 h-4 text-green-500" />
                  Mobile Number
                </label>
                <input
                  type="tel"
                  value={formData.mobile_number}
                  onChange={(e) => setFormData({ ...formData, mobile_number: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                  placeholder="Enter your mobile number (e.g. +91 9876543210)"
                />
                <p className="text-xs text-muted-foreground mt-1">This will be displayed on your card for direct calling</p>
              </div>

              {/* Privacy Settings */}
              <div className="p-4 bg-white/5 rounded-xl space-y-2">
                <label className="block text-sm font-medium mb-2">Show Contact Info on Card</label>
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.show_email}
                      onChange={(e) => setFormData({ ...formData, show_email: e.target.checked })}
                      className="w-4 h-4"
                    />
                    Email
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.show_phone}
                      onChange={(e) => setFormData({ ...formData, show_phone: e.target.checked })}
                      className="w-4 h-4"
                    />
                    Registered Phone
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.show_mobile}
                      onChange={(e) => setFormData({ ...formData, show_mobile: e.target.checked })}
                      className="w-4 h-4"
                    />
                    Mobile Number
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.show_whatsapp}
                      onChange={(e) => setFormData({ ...formData, show_whatsapp: e.target.checked })}
                      className="w-4 h-4"
                    />
                    WhatsApp
                  </label>
                </div>
                {formData.show_whatsapp && (
                  <input
                    type="text"
                    value={formData.whatsapp_number}
                    onChange={(e) => setFormData({ ...formData, whatsapp_number: e.target.value })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl mt-2"
                    placeholder="WhatsApp number with country code (e.g. 919876543210)"
                  />
                )}
              </div>

              {/* Social Links */}
              <div className="p-4 bg-white/5 rounded-xl">
                <label className="block text-sm font-medium mb-2">Social Links</label>
                <div className="flex gap-2 mb-2">
                  <select
                    value={newSocial.platform}
                    onChange={(e) => setNewSocial({ ...newSocial, platform: e.target.value })}
                    className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm"
                  >
                    {SOCIAL_PLATFORMS.map(p => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                  <input
                    type="url"
                    value={newSocial.url}
                    onChange={(e) => setNewSocial({ ...newSocial, url: e.target.value })}
                    className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm"
                    placeholder="Profile URL"
                  />
                  <button type="button" onClick={addSocialLink} className="px-3 py-2 bg-primary/20 text-primary rounded-lg">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {formData.social_links.map((link, idx) => {
                    const Icon = getSocialIcon(link.platform);
                    return (
                      <div key={idx} className="flex items-center gap-2 bg-white/10 px-3 py-2 rounded-lg">
                        <Icon className="w-4 h-4" style={{ color: getSocialColor(link.platform) }} />
                        <span className="flex-1 text-sm truncate">{link.platform}</span>
                        <button type="button" onClick={() => removeSocialLink(idx)} className="text-red-500">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Custom Links */}
              <div className="p-4 bg-white/5 rounded-xl">
                <label className="block text-sm font-medium mb-2">Custom Links</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newLink.name}
                    onChange={(e) => setNewLink({ ...newLink, name: e.target.value })}
                    className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm"
                    placeholder="Link Name"
                  />
                  <input
                    type="url"
                    value={newLink.url}
                    onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                    className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm"
                    placeholder="URL"
                  />
                  <button type="button" onClick={addCustomLink} className="px-3 py-2 bg-green-500/20 text-green-500 rounded-lg">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {formData.custom_links.map((link, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-white/10 px-3 py-2 rounded-lg">
                      <Link className="w-4 h-4 text-green-500" />
                      <span className="flex-1 text-sm">{link.name}</span>
                      <button type="button" onClick={() => removeCustomLink(idx)} className="text-red-500">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <button
                type="submit"
                disabled={saving}
                className="w-full btn-primary flex items-center justify-center gap-2"
              >
                <Save className="w-5 h-5" />
                {saving ? 'Saving...' : (card ? 'Update Card' : 'Create Card')}
              </button>
            </form>
          </div>

          {/* Preview */}
          <div>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Card Preview
            </h2>
            
            {/* Get current template style */}
            {(() => {
              const currentTemplate = TEMPLATES.find(t => t.value === formData.template);
              const templateStyle = currentTemplate?.style || 'classic';
              const is4KCosmic = templateStyle === '4k-cosmic' || templateStyle === 'cybercity';
              const isMetaverse = ['metaverse', 'cyberpunk', 'hologram', 'matrix', 'quantum', 'aurora', 'galaxy', 'sunset'].includes(templateStyle);
              const isLuxury = ['luxury', 'diamond', 'carbon'].includes(templateStyle);
              const isTech = ['tech-circuit', 'network-node', 'tech'].includes(templateStyle);
              const isBusinessGlass = templateStyle === 'business-glass';
              const isNeonRing = templateStyle === 'neon-ring';
              const isCreativeStudio = templateStyle === 'creative-studio';
              const isSplitLayout = templateStyle === 'split-layout';
              const is4KQuality = templateStyle === '4k-quality';
              const isSocialGrid = templateStyle === 'social-grid';
              
              // Helper function for social icons
              const getSocialIcon = (platform) => {
                const icons = {
                  instagram: () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>,
                  youtube: () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>,
                  facebook: () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>,
                  whatsapp: () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>,
                  twitter: () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>,
                  linkedin: () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>,
                  telegram: () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>,
                };
                return icons[platform?.toLowerCase()] || icons.instagram;
              };
              
              const getSocialColor = (platform) => {
                const colors = {
                  instagram: '#E4405F', youtube: '#FF0000', facebook: '#1877F2',
                  whatsapp: '#25D366', twitter: '#000000', linkedin: '#0A66C2', telegram: '#0088cc'
                };
                return colors[platform?.toLowerCase()] || '#888888';
              };
              
              // BUSINESS GLASS TEMPLATES - Sarah Jensen Style
              if (isBusinessGlass) {
                return (
                  <div 
                    ref={cardRef}
                    className="rounded-3xl overflow-hidden relative"
                    style={{
                      background: `linear-gradient(135deg, #0a1628 0%, #1a2744 50%, #0d1f3a 100%)`,
                      boxShadow: `0 25px 50px -12px rgba(0,0,0,0.5), 0 0 60px ${currentTemplate?.color}20`
                    }}
                  >
                    <div className="absolute inset-0 backdrop-blur-sm bg-white/5"></div>
                    <div className="relative p-6">
                      <div className="flex gap-6">
                        {/* Left - Profile Photo Frame */}
                        <div className="w-36 flex-shrink-0">
                          <div 
                            className="aspect-[3/4] rounded-2xl overflow-hidden border-4"
                            style={{ borderColor: currentTemplate?.color, boxShadow: `0 0 30px ${currentTemplate?.color}40` }}
                          >
                            {formData.profile_image ? (
                              <img src={getImageUrl(formData.profile_image)} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                                <User className="w-12 h-12 text-gray-600" />
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Right - Content */}
                        <div className="flex-1 flex flex-col justify-between">
                          <div className="flex justify-between items-start">
                            <div>
                              <h1 className="text-2xl font-black uppercase tracking-wider" style={{ color: currentTemplate?.color }}>
                                {(formData.display_name || 'Your Name').split(' ')[0]}
                              </h1>
                              <h2 className="text-2xl font-black text-white uppercase tracking-wider">
                                {(formData.display_name || '').split(' ').slice(1).join(' ')}
                              </h2>
                            </div>
                            <div className="p-2 rounded-lg bg-white" style={{ border: `2px solid ${currentTemplate?.color}` }}>
                              <QRCodeSVG value={`${window.location.origin}/card/${user?.username || ''}`} size={50} level="L" />
                            </div>
                          </div>
                          <p className="text-xs font-bold uppercase tracking-widest mt-2" style={{ color: currentTemplate?.color }}>
                            {formData.tagline || 'CREATIVE DIRECTOR | BRAND STRATEGIST'}
                          </p>
                          <p className="text-gray-400 text-xs mt-2">{formData.bio || 'Crafting exceptional visual identities.'}</p>
                          {/* Social Icons */}
                          {formData.social_links?.length > 0 && (
                            <div className="flex gap-3 mt-3">
                              {formData.social_links.slice(0, 5).map((link, idx) => {
                                const Icon = getSocialIcon(link.platform);
                                const color = getSocialColor(link.platform);
                                return (
                                  <div key={idx} className="flex flex-col items-center gap-1">
                                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}30` }}>
                                      <Icon style={{ color }} />
                                    </div>
                                    <span className="text-[7px] text-gray-500">@{link.username || link.platform}</span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                      {/* Call Now Button */}
                      {formData.show_phone && formData.mobile_number && (
                        <div 
                          className="mt-4 py-3 rounded-xl text-center font-black text-lg text-white uppercase"
                          style={{ background: `linear-gradient(90deg, ${currentTemplate?.color}, #FF8C00)`, boxShadow: `0 10px 30px ${currentTemplate?.color}40` }}
                        >
                          <Phone className="w-5 h-5 inline mr-2" /> CALL NOW
                        </div>
                      )}
                    </div>
                  </div>
                );
              }
              
              // NEON RING TEMPLATES - Alex Rivers Style
              if (isNeonRing) {
                return (
                  <div 
                    ref={cardRef}
                    className="rounded-3xl overflow-hidden relative"
                    style={{ background: `linear-gradient(180deg, #0a0a1a 0%, #1a1a2e 50%, #0f0f1f 100%)` }}
                  >
                    <div className="p-6 text-center">
                      {/* Profile Photo with Neon Ring */}
                      <div className="relative w-28 h-28 mx-auto mb-4">
                        <div className="absolute inset-0 rounded-full animate-pulse" style={{ background: `conic-gradient(from 0deg, ${currentTemplate?.color}, #A855F7, #3B82F6, ${currentTemplate?.color})`, filter: 'blur(6px)', opacity: 0.8 }} />
                        <div className="absolute inset-1 rounded-full" style={{ background: `conic-gradient(from 0deg, ${currentTemplate?.color}, #A855F7, #3B82F6, ${currentTemplate?.color})`, padding: '3px' }}>
                          <div className="w-full h-full rounded-full overflow-hidden bg-gray-900">
                            {formData.profile_image ? (
                              <img src={getImageUrl(formData.profile_image)} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center"><User className="w-10 h-10 text-gray-600" /></div>
                            )}
                          </div>
                        </div>
                      </div>
                      <h1 className="text-xl font-bold text-white mb-1">{formData.display_name || 'Your Name'}</h1>
                      <p className="text-xs font-medium mb-2" style={{ color: currentTemplate?.color }}>{formData.tagline || 'Digital Strategist'}</p>
                      <p className="text-gray-400 text-xs max-w-xs mx-auto">{formData.bio || 'Bio here'}</p>
                      {/* Social Links Grid */}
                      {formData.social_links?.length > 0 && (
                        <div className="grid grid-cols-2 gap-2 mt-4">
                          {formData.social_links.map((link, idx) => {
                            const Icon = getSocialIcon(link.platform);
                            const color = getSocialColor(link.platform);
                            return (
                              <div key={idx} className="flex items-center gap-2 p-2 rounded-lg" style={{ background: `${color}15`, border: `1px solid ${color}30` }}>
                                <Icon style={{ color }} />
                                <div className="text-left">
                                  <p className="text-white text-xs font-medium capitalize">{link.platform}</p>
                                  <p className="text-gray-500 text-[10px]">@{link.username || link.platform}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {/* Stats Row */}
                      <div className="flex justify-center gap-4 mt-4 py-3 border-t border-b border-white/10">
                        <div className="text-center"><p className="text-white font-bold text-sm">10+</p><p className="text-gray-500 text-[8px]">EXPERIENCE</p></div>
                        <div className="text-center"><p className="text-white font-bold text-sm">Featured</p><p className="text-gray-500 text-[8px]">PORTFOLIO</p></div>
                        <div className="text-center"><p className="text-white font-bold text-sm">Top</p><p className="text-gray-500 text-[8px]">AWARDS</p></div>
                      </div>
                      {/* Connect Button */}
                      <div className="mt-4 py-3 rounded-lg text-center font-bold text-white uppercase text-sm" style={{ background: `linear-gradient(90deg, ${currentTemplate?.color}80, #A855F780)` }}>
                        CONNECT
                      </div>
                    </div>
                  </div>
                );
              }
              
              // CREATIVE STUDIO TEMPLATES - Maya Sharma Style
              if (isCreativeStudio) {
                return (
                  <div 
                    ref={cardRef}
                    className="rounded-3xl overflow-hidden relative"
                    style={{ background: `linear-gradient(135deg, #0f0a2a 0%, #1a1040 50%, #0d0820 100%)`, boxShadow: `0 25px 50px -12px rgba(0,0,0,0.5)` }}
                  >
                    {/* Abstract shapes */}
                    <div className="absolute inset-0 overflow-hidden opacity-30">
                      <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full" style={{ background: `radial-gradient(circle, ${currentTemplate?.color}40, transparent)` }} />
                      <div className="absolute bottom-16 -left-16 w-32 h-32 rounded-full" style={{ background: `radial-gradient(circle, #A855F740, transparent)` }} />
                    </div>
                    <div className="relative p-6 text-center">
                      {/* Logo Badge */}
                      <div className="absolute top-3 left-3 flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: currentTemplate?.color }}>
                          <Star className="w-3 h-3 text-white" />
                        </div>
                        <span className="text-white font-bold text-xs">CREATIVE VISIONS</span>
                      </div>
                      {/* Profile Photo */}
                      <div className="relative w-36 h-44 mx-auto mt-8 mb-4">
                        <div className="absolute inset-0 rounded-xl" style={{ background: `linear-gradient(135deg, ${currentTemplate?.color}, #A855F7)`, filter: 'blur(12px)', opacity: 0.6 }} />
                        <div className="absolute inset-0 rounded-xl p-1" style={{ background: `linear-gradient(135deg, ${currentTemplate?.color}, #A855F7)` }}>
                          <div className="w-full h-full rounded-lg overflow-hidden bg-gray-900">
                            {formData.profile_image ? (
                              <img src={getImageUrl(formData.profile_image)} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center"><User className="w-16 h-16 text-gray-600" /></div>
                            )}
                          </div>
                        </div>
                      </div>
                      <h1 className="text-xl font-black text-white uppercase tracking-wider">{formData.display_name || 'Your Name'}</h1>
                      <p className="text-xs font-bold uppercase tracking-widest mt-1" style={{ color: currentTemplate?.color }}>{formData.tagline || 'CREATIVE DIRECTOR'}</p>
                      <p className="text-gray-400 text-xs italic mt-2">"{formData.bio || 'Crafting Experiences'}"</p>
                      {/* Social Links */}
                      {formData.social_links?.length > 0 && (
                        <div className="grid grid-cols-2 gap-2 mt-4">
                          {formData.social_links.slice(0, 4).map((link, idx) => {
                            const Icon = getSocialIcon(link.platform);
                            const color = getSocialColor(link.platform);
                            return (
                              <div key={idx} className="flex items-center gap-2 px-3 py-2 rounded-full" style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)' }}>
                                <Icon style={{ color }} />
                                <span className="text-white text-xs">/{link.username || link.platform}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {/* Call Button */}
                      <div className="mt-4 py-3 rounded-full text-center font-black text-lg text-white uppercase" style={{ background: `linear-gradient(90deg, #FF6B6B, ${currentTemplate?.color})` }}>
                        <Phone className="w-4 h-4 inline mr-2" /> CALL ME NOW
                      </div>
                    </div>
                  </div>
                );
              }
              
              // 4K COSMIC / CYBERCITY TEMPLATES - Premium Futuristic Design
              if (is4KCosmic) {
                // Build card data for the component
                const cardData = {
                  display_name: formData.display_name || user?.name || 'Your Name',
                  tagline: formData.tagline,
                  bio: formData.bio,
                  profile_image: formData.profile_image ? getImageUrl(formData.profile_image) : null,
                  social_links: formData.social_links || [],
                  show_phone: formData.show_phone,
                  phone: formData.mobile_number || user?.mobile,
                  address: formData.tagline || 'Digital Entrepreneur',
                };
                
                // Map template value to variant
                const variantMap = {
                  'cosmic-4k-galaxy': 'galaxy',
                  'cosmic-4k-nebula': 'nebula',
                  'cosmic-4k-aurora': 'aurora',
                  'cosmic-4k-sunrise': 'sunrise',
                  'cosmic-4k-midnight': 'midnight',
                };
                const variant = variantMap[formData.template] || 'galaxy';
                
                return (
                  <div ref={cardRef}>
                    <Cosmic4KDVCTemplate 
                      card={cardData}
                      variant={variant}
                    />
                  </div>
                );
              }
              
              // METAVERSE TEMPLATES - Ultra Modern Hyper Design
              if (isMetaverse) {
                return (
                  <div 
                    ref={cardRef}
                    className="rounded-3xl overflow-hidden shadow-2xl relative"
                    style={{ 
                      backgroundColor: currentTemplate?.bg || '#0a0014',
                      minHeight: '550px'
                    }}
                  >
                    {/* Animated Background Effects */}
                    <div className="absolute inset-0 overflow-hidden">
                      {/* Grid overlay */}
                      <div className="absolute inset-0 opacity-20" style={{
                        backgroundImage: `linear-gradient(${currentTemplate?.color}20 1px, transparent 1px), linear-gradient(90deg, ${currentTemplate?.color}20 1px, transparent 1px)`,
                        backgroundSize: '30px 30px'
                      }} />
                      
                      {/* Glowing orbs */}
                      <div 
                        className="absolute w-[400px] h-[400px] rounded-full blur-[100px] animate-pulse"
                        style={{ 
                          background: `radial-gradient(circle, ${currentTemplate?.color}40 0%, transparent 70%)`,
                          top: '-100px',
                          left: '50%',
                          transform: 'translateX(-50%)'
                        }}
                      />
                      <div 
                        className="absolute w-[300px] h-[300px] rounded-full blur-[80px]"
                        style={{ 
                          background: `radial-gradient(circle, ${currentTemplate?.color}30 0%, transparent 70%)`,
                          bottom: '-50px',
                          right: '-50px',
                          animation: 'pulse 3s ease-in-out infinite alternate'
                        }}
                      />
                      
                      {/* Scan lines for cyberpunk */}
                      {templateStyle === 'cyberpunk' && (
                        <div className="absolute inset-0 pointer-events-none opacity-30" style={{
                          background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,0,255,0.1) 2px, rgba(255,0,255,0.1) 4px)'
                        }} />
                      )}
                      
                      {/* Matrix rain effect */}
                      {templateStyle === 'matrix' && (
                        <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-40">
                          {[...Array(15)].map((_, i) => (
                            <div
                              key={i}
                              className="absolute text-green-500 text-xs font-mono whitespace-pre"
                              style={{
                                left: `${i * 7}%`,
                                animation: `matrixFall ${2 + Math.random() * 3}s linear infinite`,
                                animationDelay: `${Math.random() * 2}s`
                              }}
                            >
                              {Array.from({ length: 15 }, () => String.fromCharCode(0x30A0 + Math.random() * 96)).join('\n')}
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Hologram lines */}
                      {templateStyle === 'hologram' && (
                        <div className="absolute inset-0 pointer-events-none">
                          {[...Array(5)].map((_, i) => (
                            <div
                              key={i}
                              className="absolute w-full h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent"
                              style={{
                                top: `${20 + i * 15}%`,
                                animation: `hologramScan ${3 + i * 0.5}s ease-in-out infinite`,
                                opacity: 0.4
                              }}
                            />
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="relative z-10 px-8 py-10 text-center">
                      {/* STUNNING PROFILE FRAME - Diamond/Hexagon Style */}
                      <div className="relative w-52 h-52 mx-auto mb-8">
                        {/* Multiple rotating rings */}
                        <div 
                          className="absolute inset-0"
                          style={{ 
                            background: `conic-gradient(from 0deg, ${currentTemplate?.color}00, ${currentTemplate?.color}60, ${currentTemplate?.color}00)`,
                            borderRadius: '50%',
                            animation: 'spin 8s linear infinite'
                          }}
                        />
                        <div 
                          className="absolute inset-2"
                          style={{ 
                            background: `conic-gradient(from 180deg, ${currentTemplate?.color}00, ${currentTemplate?.color}40, ${currentTemplate?.color}00)`,
                            borderRadius: '50%',
                            animation: 'spin 6s linear infinite reverse'
                          }}
                        />
                        
                        {/* Diamond shape frame */}
                        <div 
                          className="absolute inset-4"
                          style={{
                            background: `linear-gradient(135deg, ${currentTemplate?.color}20, transparent, ${currentTemplate?.color}20)`,
                            borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
                            border: `2px solid ${currentTemplate?.color}50`,
                            boxShadow: `0 0 40px ${currentTemplate?.color}30, inset 0 0 40px ${currentTemplate?.color}10`,
                            animation: 'morphShape 8s ease-in-out infinite'
                          }}
                        />
                        
                        {/* Pulsing glow ring */}
                        <div 
                          className="absolute inset-6 rounded-full"
                          style={{ 
                            border: `3px solid ${currentTemplate?.color}`,
                            boxShadow: `0 0 20px ${currentTemplate?.color}, 0 0 40px ${currentTemplate?.color}50, 0 0 60px ${currentTemplate?.color}30`,
                            animation: 'pulseGlow 2s ease-in-out infinite'
                          }}
                        />
                        
                        {/* Inner hexagon */}
                        <div 
                          className="absolute inset-8 overflow-hidden flex items-center justify-center"
                          style={{ 
                            backgroundColor: currentTemplate?.bg,
                            clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
                            boxShadow: `inset 0 0 30px ${currentTemplate?.color}20`
                          }}
                        >
                          {formData.profile_image ? (
                            <img src={getImageUrl(formData.profile_image)} alt="Profile" className="w-full h-full object-cover scale-125" />
                          ) : (
                            <User className="w-16 h-16" style={{ color: currentTemplate?.color }} />
                          )}
                        </div>
                        
                        {/* Orbiting particles */}
                        {[...Array(12)].map((_, i) => (
                          <div
                            key={i}
                            className="absolute"
                            style={{
                              width: `${4 + (i % 3) * 2}px`,
                              height: `${4 + (i % 3) * 2}px`,
                              backgroundColor: currentTemplate?.color,
                              borderRadius: '50%',
                              left: '50%',
                              top: '50%',
                              transform: `rotate(${i * 30}deg) translateX(${85 + (i % 2) * 10}px) translateY(-50%)`,
                              animation: `orbitParticle ${10 + i}s linear infinite`,
                              opacity: 0.4 + (i % 3) * 0.2,
                              boxShadow: `0 0 ${6 + i}px ${currentTemplate?.color}`
                            }}
                          />
                        ))}
                        
                        {/* Corner accent lights */}
                        <div 
                          className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 rounded-full"
                          style={{ 
                            backgroundColor: currentTemplate?.color,
                            boxShadow: `0 0 20px ${currentTemplate?.color}`,
                            animation: 'twinkle 1.5s ease-in-out infinite'
                          }}
                        />
                        <div 
                          className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 rounded-full"
                          style={{ 
                            backgroundColor: currentTemplate?.color,
                            boxShadow: `0 0 20px ${currentTemplate?.color}`,
                            animation: 'twinkle 1.5s ease-in-out infinite 0.75s'
                          }}
                        />
                      </div>
                      
                      {/* Name with enhanced glow and animation */}
                      <h3 
                        className="text-4xl font-black mb-3 tracking-wide relative"
                        style={{ 
                          color: currentTemplate?.color,
                          textShadow: `0 0 30px ${currentTemplate?.color}80, 0 0 60px ${currentTemplate?.color}40, 0 0 90px ${currentTemplate?.color}20`,
                          animation: 'textGlow 3s ease-in-out infinite'
                        }}
                      >
                        {formData.display_name || 'YOUR NAME'}
                        {/* Underline accent */}
                        <span 
                          className="absolute -bottom-1 left-1/2 -translate-x-1/2 h-1 rounded-full"
                          style={{
                            width: '60%',
                            background: `linear-gradient(90deg, transparent, ${currentTemplate?.color}, transparent)`,
                            boxShadow: `0 0 10px ${currentTemplate?.color}`
                          }}
                        />
                      </h3>
                      
                      {/* Tagline with badge style */}
                      {formData.tagline && (
                        <div 
                          className="inline-block px-6 py-2 rounded-full mb-4"
                          style={{ 
                            background: `linear-gradient(135deg, ${currentTemplate?.color}20, ${currentTemplate?.color}05)`,
                            border: `1px solid ${currentTemplate?.color}40`,
                            boxShadow: `0 0 20px ${currentTemplate?.color}15`
                          }}
                        >
                          <p 
                            className="text-sm font-bold tracking-[0.25em] uppercase"
                            style={{ color: currentTemplate?.color }}
                          >
                            {formData.tagline}
                          </p>
                        </div>
                      )}
                      
                      {/* Bio with glass effect */}
                      {formData.bio && (
                        <div 
                          className="max-w-sm mx-auto mb-8 p-4 rounded-2xl"
                          style={{
                            background: `linear-gradient(135deg, ${currentTemplate?.color}08, transparent)`,
                            border: `1px solid ${currentTemplate?.color}15`
                          }}
                        >
                          <p className="text-gray-300 text-sm leading-relaxed">{formData.bio}</p>
                        </div>
                      )}

                      {/* Decorative divider with icons */}
                      <div className="flex items-center justify-center gap-4 mb-8">
                        <div 
                          className="w-20 h-px"
                          style={{ 
                            background: `linear-gradient(90deg, transparent, ${currentTemplate?.color}60)`,
                          }}
                        />
                        <div 
                          className="w-3 h-3 rotate-45"
                          style={{ 
                            backgroundColor: currentTemplate?.color,
                            boxShadow: `0 0 15px ${currentTemplate?.color}`
                          }}
                        />
                        <div 
                          className="w-20 h-px"
                          style={{ 
                            background: `linear-gradient(90deg, ${currentTemplate?.color}60, transparent)`,
                          }}
                        />
                      </div>

                      {/* FLOATING SOCIAL ICONS - Stunning Metaverse Style */}
                      {formData.social_links.length > 0 && (
                        <div className="relative py-8 mb-6">
                          {/* Orbiting container */}
                          <div className="relative w-48 h-48 mx-auto">
                            {/* Central glow */}
                            <div 
                              className="absolute inset-0 rounded-full animate-pulse"
                              style={{
                                background: `radial-gradient(circle, ${currentTemplate?.color}20 0%, transparent 70%)`
                              }}
                            />
                            
                            {/* Orbit rings */}
                            <div 
                              className="absolute inset-4 rounded-full border opacity-30"
                              style={{ 
                                borderColor: currentTemplate?.color,
                                animation: 'spin 20s linear infinite'
                              }}
                            />
                            <div 
                              className="absolute inset-8 rounded-full border opacity-20"
                              style={{ 
                                borderColor: currentTemplate?.color,
                                animation: 'spin 15s linear infinite reverse'
                              }}
                            />
                            
                            {/* Floating social icons */}
                            {formData.social_links.slice(0, 6).map((link, idx) => {
                              const Icon = getSocialIcon(link.platform);
                              const socialColor = getSocialColor(link.platform);
                              const totalIcons = Math.min(formData.social_links.length, 6);
                              const angle = (idx / totalIcons) * 360;
                              const radius = 75;
                              const x = Math.cos((angle - 90) * Math.PI / 180) * radius;
                              const y = Math.sin((angle - 90) * Math.PI / 180) * radius;
                              
                              return (
                                <a
                                  key={idx}
                                  href={link.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="absolute w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-500 hover:scale-125 hover:z-20 group"
                                  style={{ 
                                    left: `calc(50% + ${x}px - 28px)`,
                                    top: `calc(50% + ${y}px - 28px)`,
                                    backgroundColor: `${socialColor}15`,
                                    border: `2px solid ${socialColor}60`,
                                    boxShadow: `0 0 25px ${socialColor}40, 0 0 50px ${socialColor}20, inset 0 0 15px ${socialColor}15`,
                                    animation: `floatIcon ${3 + idx * 0.5}s ease-in-out infinite`,
                                    animationDelay: `${idx * 0.3}s`
                                  }}
                                >
                                  {/* Glow ring on hover */}
                                  <div 
                                    className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity"
                                    style={{
                                      boxShadow: `0 0 30px ${socialColor}, 0 0 60px ${socialColor}50`
                                    }}
                                  />
                                  {/* Icon */}
                                  <Icon 
                                    className="w-7 h-7 relative z-10 transition-transform group-hover:scale-110" 
                                    style={{ 
                                      color: socialColor,
                                      filter: `drop-shadow(0 0 8px ${socialColor})`
                                    }} 
                                  />
                                  {/* Particle trail */}
                                  <div 
                                    className="absolute w-2 h-2 rounded-full opacity-60"
                                    style={{
                                      backgroundColor: socialColor,
                                      animation: 'particleTrail 2s ease-out infinite',
                                      animationDelay: `${idx * 0.2}s`
                                    }}
                                  />
                                </a>
                              );
                            })}
                            
                            {/* Center icon - Connect */}
                            <div 
                              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full flex items-center justify-center"
                              style={{
                                background: `linear-gradient(135deg, ${currentTemplate?.color}30, ${currentTemplate?.color}10)`,
                                border: `2px solid ${currentTemplate?.color}50`,
                                boxShadow: `0 0 30px ${currentTemplate?.color}40`
                              }}
                            >
                              <span className="text-2xl">🔗</span>
                            </div>
                          </div>
                          
                          {/* Connect label */}
                          <p 
                            className="text-center text-sm font-bold tracking-widest uppercase mt-4"
                            style={{ 
                              color: currentTemplate?.color,
                              textShadow: `0 0 20px ${currentTemplate?.color}60`
                            }}
                          >
                            Connect With Me
                          </p>
                        </div>
                      )}

                      {/* Mobile Number - Neon Button */}
                      {formData.show_mobile && formData.mobile_number && (
                        <a 
                          href={`tel:${formData.mobile_number.replace(/\s/g, '')}`}
                          className="inline-flex items-center gap-3 px-6 py-3 rounded-full text-sm font-bold transition-all hover:scale-105"
                          style={{ 
                            background: `linear-gradient(135deg, ${currentTemplate?.color}30, ${currentTemplate?.color}10)`,
                            border: `2px solid ${currentTemplate?.color}60`,
                            color: currentTemplate?.color,
                            boxShadow: `0 0 20px ${currentTemplate?.color}30`
                          }}
                        >
                          <Phone className="w-5 h-5" />
                          {formData.mobile_number}
                        </a>
                      )}

                      {/* Custom Links - Neon Buttons */}
                      {formData.custom_links.length > 0 && (
                        <div className="mt-6 space-y-3 max-w-xs mx-auto">
                          {formData.custom_links.map((link, idx) => (
                            <a
                              key={idx}
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block px-5 py-4 rounded-xl text-center font-bold transition-all hover:scale-[1.02] hover:-translate-y-1"
                              style={{ 
                                background: `linear-gradient(135deg, ${currentTemplate?.color}20, transparent)`,
                                border: `1px solid ${currentTemplate?.color}40`,
                                color: currentTemplate?.color,
                                boxShadow: `0 4px 20px ${currentTemplate?.color}20`
                              }}
                            >
                              {link.name}
                            </a>
                          ))}
                        </div>
                      )}

                      {/* Referral Code - Futuristic Badge */}
                      <div className="mt-8 pt-6 border-t border-white/10">
                        <p 
                          className="text-xs mb-4 tracking-[0.3em] uppercase font-bold"
                          style={{ color: `${currentTemplate?.color}60` }}
                        >
                          Join My Network
                        </p>
                        <div 
                          className="inline-block px-10 py-5 rounded-3xl relative overflow-hidden"
                          style={{ 
                            background: `linear-gradient(135deg, ${currentTemplate?.color}15, ${currentTemplate?.color}05)`,
                            border: `2px solid ${currentTemplate?.color}40`,
                            boxShadow: `0 0 40px ${currentTemplate?.color}20, inset 0 0 30px ${currentTemplate?.color}10`
                          }}
                        >
                          {/* Animated shine effect */}
                          <div 
                            className="absolute inset-0 opacity-30"
                            style={{
                              background: `linear-gradient(45deg, transparent 40%, ${currentTemplate?.color}40 50%, transparent 60%)`,
                              backgroundSize: '200% 200%',
                              animation: 'shine 3s ease-in-out infinite'
                            }}
                          />
                          <p className="text-[10px] text-gray-400 mb-2 tracking-[0.2em] font-medium relative z-10">REFERRAL CODE</p>
                          <p 
                            className="font-mono font-black text-3xl tracking-wider relative z-10"
                            style={{ 
                              color: currentTemplate?.color,
                              textShadow: `0 0 30px ${currentTemplate?.color}80, 0 0 60px ${currentTemplate?.color}40`
                            }}
                          >
                            {user?.username || 'YOUR-CODE'}
                          </p>
                          {/* Copy hint */}
                          <p className="text-[9px] text-gray-500 mt-2 relative z-10">Tap to copy</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* CSS Animations */}
                    <style>{`
                      @keyframes matrixFall {
                        from { transform: translateY(-100%); }
                        to { transform: translateY(100vh); }
                      }
                      @keyframes hologramScan {
                        0%, 100% { opacity: 0.2; transform: translateY(0); }
                        50% { opacity: 0.6; transform: translateY(10px); }
                      }
                      @keyframes float {
                        0%, 100% { transform: translate(-50%, -50%) translateY(0); }
                        50% { transform: translate(-50%, -50%) translateY(-10px); }
                      }
                      @keyframes spin {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                      }
                      @keyframes floatIcon {
                        0%, 100% { 
                          transform: translateY(0) scale(1);
                          filter: brightness(1);
                        }
                        50% { 
                          transform: translateY(-12px) scale(1.05);
                          filter: brightness(1.2);
                        }
                      }
                      @keyframes particleTrail {
                        0% { 
                          transform: translate(0, 0) scale(1);
                          opacity: 0.8;
                        }
                        100% { 
                          transform: translate(-20px, 20px) scale(0);
                          opacity: 0;
                        }
                      }
                      @keyframes orbitPulse {
                        0%, 100% { 
                          transform: scale(1);
                          opacity: 0.3;
                        }
                        50% { 
                          transform: scale(1.1);
                          opacity: 0.5;
                        }
                      }
                      @keyframes glowPulse {
                        0%, 100% { 
                          box-shadow: 0 0 20px currentColor, 0 0 40px currentColor;
                        }
                        50% { 
                          box-shadow: 0 0 40px currentColor, 0 0 80px currentColor;
                        }
                      }
                      @keyframes morphShape {
                        0%, 100% { border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%; }
                        25% { border-radius: 58% 42% 75% 25% / 76% 46% 54% 24%; }
                        50% { border-radius: 50% 50% 33% 67% / 55% 27% 73% 45%; }
                        75% { border-radius: 33% 67% 58% 42% / 63% 68% 32% 37%; }
                      }
                      @keyframes pulseGlow {
                        0%, 100% { 
                          transform: scale(1);
                          opacity: 1;
                        }
                        50% { 
                          transform: scale(1.05);
                          opacity: 0.8;
                        }
                      }
                      @keyframes orbitParticle {
                        from { transform: rotate(0deg) translateX(85px) translateY(-50%); }
                        to { transform: rotate(360deg) translateX(85px) translateY(-50%); }
                      }
                      @keyframes twinkle {
                        0%, 100% { opacity: 1; transform: scale(1); }
                        50% { opacity: 0.5; transform: scale(0.8); }
                      }
                      @keyframes textGlow {
                        0%, 100% { filter: brightness(1); }
                        50% { filter: brightness(1.2); }
                      }
                      @keyframes shine {
                        0% { background-position: 200% 0; }
                        100% { background-position: -200% 0; }
                      }
                    `}</style>
                  </div>
                );
              }
              
              // LUXURY TEMPLATES
              if (isLuxury) {
                return (
                  <div 
                    ref={cardRef}
                    className="rounded-3xl overflow-hidden shadow-2xl relative"
                    style={{ 
                      backgroundColor: currentTemplate?.bg || '#1a1000',
                      minHeight: '500px'
                    }}
                  >
                    {/* Luxury Background */}
                    <div className="absolute inset-0">
                      {/* Shimmer effect */}
                      <div 
                        className="absolute inset-0"
                        style={{
                          background: `linear-gradient(45deg, transparent 40%, ${currentTemplate?.color}10 50%, transparent 60%)`,
                          backgroundSize: '200% 200%',
                          animation: 'shimmer 3s ease-in-out infinite'
                        }}
                      />
                      {/* Corner ornaments */}
                      <div className="absolute top-0 left-0 w-24 h-24 border-t-2 border-l-2" style={{ borderColor: `${currentTemplate?.color}40` }} />
                      <div className="absolute top-0 right-0 w-24 h-24 border-t-2 border-r-2" style={{ borderColor: `${currentTemplate?.color}40` }} />
                      <div className="absolute bottom-0 left-0 w-24 h-24 border-b-2 border-l-2" style={{ borderColor: `${currentTemplate?.color}40` }} />
                      <div className="absolute bottom-0 right-0 w-24 h-24 border-b-2 border-r-2" style={{ borderColor: `${currentTemplate?.color}40` }} />
                    </div>

                    {/* Content */}
                    <div className="relative z-10 px-8 py-10 text-center">
                      {/* Premium Profile Frame */}
                      <div className="relative w-40 h-40 mx-auto mb-6">
                        <div 
                          className="absolute inset-0 rounded-full"
                          style={{ 
                            border: `4px solid ${currentTemplate?.color}`,
                            boxShadow: `0 0 40px ${currentTemplate?.color}40`
                          }}
                        />
                        <div 
                          className="absolute inset-2 rounded-full overflow-hidden"
                          style={{ backgroundColor: currentTemplate?.bg }}
                        >
                          {formData.profile_image ? (
                            <img src={getImageUrl(formData.profile_image)} alt="Profile" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <User className="w-16 h-16" style={{ color: currentTemplate?.color }} />
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Name */}
                      <h3 
                        className="text-3xl font-serif font-bold mb-2"
                        style={{ color: currentTemplate?.color }}
                      >
                        {formData.display_name || 'Your Name'}
                      </h3>
                      
                      {formData.tagline && (
                        <p className="text-sm tracking-[0.2em] uppercase mb-4" style={{ color: `${currentTemplate?.color}80` }}>
                          {formData.tagline}
                        </p>
                      )}
                      
                      {formData.bio && (
                        <p className="text-gray-400 text-sm max-w-xs mx-auto mb-6">{formData.bio}</p>
                      )}

                      {/* Elegant divider */}
                      <div className="flex items-center justify-center gap-4 mb-6">
                        <div className="w-16 h-px" style={{ backgroundColor: `${currentTemplate?.color}40` }} />
                        <div className="w-2 h-2 rotate-45" style={{ backgroundColor: currentTemplate?.color }} />
                        <div className="w-16 h-px" style={{ backgroundColor: `${currentTemplate?.color}40` }} />
                      </div>

                      {/* Social Links */}
                      {formData.social_links.length > 0 && (
                        <div className="flex justify-center gap-3 mb-6 flex-wrap">
                          {formData.social_links.map((link, idx) => {
                            const Icon = getSocialIcon(link.platform);
                            return (
                              <a
                                key={idx}
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-11 h-11 rounded-full flex items-center justify-center transition-all hover:scale-110"
                                style={{ 
                                  backgroundColor: `${getSocialColor(link.platform)}20`,
                                  border: `1px solid ${getSocialColor(link.platform)}40`
                                }}
                              >
                                <Icon className="w-5 h-5" style={{ color: getSocialColor(link.platform) }} />
                              </a>
                            );
                          })}
                        </div>
                      )}

                      {formData.show_mobile && formData.mobile_number && (
                        <a 
                          href={`tel:${formData.mobile_number.replace(/\s/g, '')}`}
                          className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all hover:scale-105"
                          style={{ 
                            border: `2px solid ${currentTemplate?.color}60`,
                            color: currentTemplate?.color
                          }}
                        >
                          <Phone className="w-4 h-4" />
                          {formData.mobile_number}
                        </a>
                      )}

                      {formData.custom_links.length > 0 && (
                        <div className="mt-6 space-y-2 max-w-xs mx-auto">
                          {formData.custom_links.map((link, idx) => (
                            <a
                              key={idx}
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block px-5 py-3 rounded-xl text-center font-medium transition-all hover:scale-[1.02]"
                              style={{ 
                                border: `1px solid ${currentTemplate?.color}40`,
                                color: currentTemplate?.color
                              }}
                            >
                              {link.name}
                            </a>
                          ))}
                        </div>
                      )}

                      <div className="mt-8 pt-4 border-t" style={{ borderColor: `${currentTemplate?.color}20` }}>
                        <p className="text-xs tracking-widest uppercase mb-2" style={{ color: `${currentTemplate?.color}60` }}>Referral</p>
                        <p className="font-serif font-bold text-xl" style={{ color: currentTemplate?.color }}>
                          {user?.username || 'YOUR-CODE'}
                        </p>
                      </div>
                    </div>
                    
                    <style>{`
                      @keyframes shimmer {
                        0% { background-position: 200% 0; }
                        100% { background-position: -200% 0; }
                      }
                    `}</style>
                  </div>
                );
              }
              
              // TECH TEMPLATES (existing)
              if (isTech) {
                return (
                  <div 
                    ref={cardRef}
                    className="rounded-2xl overflow-hidden shadow-2xl relative"
                    style={{ 
                      backgroundColor: formData.background_color,
                      minHeight: '500px'
                    }}
                  >
                {/* SVG Background Pattern */}
                <svg className="absolute inset-0 w-full h-full opacity-30" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    {TEMPLATES.find(t => t.value === formData.template)?.style === 'tech-circuit' ? (
                      <>
                        {/* Circuit pattern */}
                        <pattern id="circuit" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
                          <path d="M10 10h80v80h-80z" fill="none" stroke={formData.primary_color} strokeWidth="0.5" opacity="0.3"/>
                          <circle cx="10" cy="10" r="3" fill={formData.primary_color} opacity="0.5"/>
                          <circle cx="90" cy="10" r="3" fill={formData.primary_color} opacity="0.5"/>
                          <circle cx="10" cy="90" r="3" fill={formData.primary_color} opacity="0.5"/>
                          <circle cx="90" cy="90" r="3" fill={formData.primary_color} opacity="0.5"/>
                          <line x1="50" y1="10" x2="50" y2="90" stroke={formData.primary_color} strokeWidth="0.5" opacity="0.3"/>
                          <line x1="10" y1="50" x2="90" y2="50" stroke={formData.primary_color} strokeWidth="0.5" opacity="0.3"/>
                        </pattern>
                      </>
                    ) : (
                      <>
                        {/* Network nodes pattern */}
                        <pattern id="network" x="0" y="0" width="150" height="150" patternUnits="userSpaceOnUse">
                          <circle cx="25" cy="25" r="4" fill={formData.primary_color} opacity="0.6"/>
                          <circle cx="125" cy="25" r="3" fill={formData.primary_color} opacity="0.4"/>
                          <circle cx="75" cy="75" r="5" fill={formData.primary_color} opacity="0.7"/>
                          <circle cx="25" cy="125" r="3" fill={formData.primary_color} opacity="0.4"/>
                          <circle cx="125" cy="125" r="4" fill={formData.primary_color} opacity="0.5"/>
                          <line x1="25" y1="25" x2="75" y2="75" stroke={formData.primary_color} strokeWidth="1" opacity="0.3"/>
                          <line x1="125" y1="25" x2="75" y2="75" stroke={formData.primary_color} strokeWidth="1" opacity="0.3"/>
                          <line x1="25" y1="125" x2="75" y2="75" stroke={formData.primary_color} strokeWidth="1" opacity="0.3"/>
                          <line x1="125" y1="125" x2="75" y2="75" stroke={formData.primary_color} strokeWidth="1" opacity="0.3"/>
                        </pattern>
                      </>
                    )}
                    <radialGradient id="glow" cx="50%" cy="50%" r="50%">
                      <stop offset="0%" stopColor={formData.primary_color} stopOpacity="0.4"/>
                      <stop offset="100%" stopColor={formData.primary_color} stopOpacity="0"/>
                    </radialGradient>
                  </defs>
                  <rect width="100%" height="100%" fill={`url(#${TEMPLATES.find(t => t.value === formData.template)?.style === 'tech-circuit' ? 'circuit' : 'network'})`}/>
                  {/* Central glow */}
                  <ellipse cx="50%" cy="35%" rx="150" ry="150" fill="url(#glow)"/>
                </svg>

                {/* Content */}
                <div className="relative z-10 px-6 py-8 text-center">
                  {/* Decorative rings around profile */}
                  <div className="relative w-48 h-48 mx-auto mb-6">
                    {/* Outer glow ring */}
                    <div 
                      className="absolute inset-0 rounded-full animate-pulse"
                      style={{ 
                        background: `radial-gradient(circle, ${formData.primary_color}40 0%, transparent 70%)`,
                        transform: 'scale(1.3)'
                      }}
                    />
                    {/* Decorative border ring */}
                    <div 
                      className="absolute inset-0 rounded-full"
                      style={{ 
                        border: `3px solid ${formData.primary_color}`,
                        boxShadow: `0 0 30px ${formData.primary_color}60, inset 0 0 30px ${formData.primary_color}30`
                      }}
                    />
                    {/* Inner decorative ring */}
                    <div 
                      className="absolute inset-2 rounded-full"
                      style={{ border: `1px solid ${formData.primary_color}50` }}
                    />
                    {/* Profile image container */}
                    <div 
                      className="absolute inset-4 rounded-full overflow-hidden flex items-center justify-center"
                      style={{ 
                        backgroundColor: formData.background_color,
                        border: `2px solid ${formData.primary_color}80`
                      }}
                    >
                      {formData.profile_image ? (
                        <img src={getImageUrl(formData.profile_image)} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-16 h-16" style={{ color: formData.primary_color }} />
                      )}
                    </div>
                    
                    {/* Floating social icons around the profile */}
                    {formData.social_links.slice(0, 8).map((link, idx) => {
                      const Icon = getSocialIcon(link.platform);
                      const angle = (idx * 45 - 90) * (Math.PI / 180);
                      const radius = 110;
                      const x = Math.cos(angle) * radius;
                      const y = Math.sin(angle) * radius;
                      return (
                        <a
                          key={idx}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="absolute w-10 h-10 rounded-full flex items-center justify-center transition-all hover:scale-125"
                          style={{ 
                            backgroundColor: `${getSocialColor(link.platform)}30`,
                            border: `1px solid ${getSocialColor(link.platform)}60`,
                            left: `calc(50% + ${x}px - 20px)`,
                            top: `calc(50% + ${y}px - 20px)`,
                            boxShadow: `0 0 15px ${getSocialColor(link.platform)}40`
                          }}
                        >
                          <Icon className="w-5 h-5" style={{ color: getSocialColor(link.platform) }} />
                        </a>
                      );
                    })}
                  </div>
                  
                  {/* Name */}
                  <h3 
                    className="text-3xl font-bold mb-2"
                    style={{ 
                      color: formData.primary_color,
                      textShadow: `0 0 20px ${formData.primary_color}60`
                    }}
                  >
                    {formData.display_name || 'Your Name'}
                  </h3>
                  
                  {/* Tagline */}
                  {formData.tagline && (
                    <p className="text-gray-300 text-sm mb-2">{formData.tagline}</p>
                  )}
                  
                  {/* Bio */}
                  {formData.bio && (
                    <p className="text-gray-400 text-sm max-w-xs mx-auto mb-4">{formData.bio}</p>
                  )}

                  {/* Mobile Number */}
                  {formData.show_mobile && formData.mobile_number && (
                    <a 
                      href={`tel:${formData.mobile_number.replace(/\s/g, '')}`}
                      className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium transition-all hover:scale-105"
                      style={{ 
                        backgroundColor: `${formData.primary_color}20`,
                        border: `1px solid ${formData.primary_color}40`,
                        color: formData.primary_color 
                      }}
                    >
                      <Phone className="w-4 h-4" />
                      {formData.mobile_number}
                    </a>
                  )}

                  {/* Custom Links */}
                  {formData.custom_links.length > 0 && (
                    <div className="mt-6 space-y-2 max-w-xs mx-auto">
                      {formData.custom_links.map((link, idx) => (
                        <a
                          key={idx}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block px-4 py-3 rounded-xl text-center font-medium transition-all hover:scale-[1.02]"
                          style={{ 
                            backgroundColor: `${formData.primary_color}15`,
                            border: `1px solid ${formData.primary_color}30`,
                            color: formData.primary_color
                          }}
                        >
                          {link.name}
                        </a>
                      ))}
                    </div>
                  )}

                  {/* Referral Section */}
                  <div className="mt-8 pt-4 border-t border-white/10">
                    <p className="text-xs text-gray-500 mb-3">Join My Team</p>
                    <div 
                      className="inline-block px-6 py-2 rounded-full"
                      style={{ 
                        backgroundColor: `${formData.primary_color}20`,
                        border: `1px solid ${formData.primary_color}40`
                      }}
                    >
                      <p className="text-[10px] text-gray-400">Referral Code</p>
                      <p className="font-mono font-bold text-lg" style={{ color: formData.primary_color }}>
                        {user?.username || 'YOUR-CODE'}
                      </p>
                    </div>
                  </div>
                </div>
                  </div>
                );
              }
              
              // CLASSIC TEMPLATE (default fallback)
              return (
              <div 
                ref={cardRef}
                className="rounded-2xl overflow-hidden shadow-2xl"
                style={{ backgroundColor: formData.background_color }}
              >
                {/* Cover Image */}
                <div 
                  className="h-32 bg-gradient-to-r"
                  style={{ 
                    backgroundImage: formData.cover_image ? `url(${getImageUrl(formData.cover_image)})` : undefined,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    background: !formData.cover_image ? `linear-gradient(135deg, ${formData.primary_color}40, ${formData.primary_color}10)` : undefined
                  }}
                />
                
                {/* Profile Section */}
                <div className="px-6 pb-6 -mt-12 text-center">
                  <div 
                    className="w-24 h-24 rounded-full mx-auto mb-4 border-4 bg-gray-800 flex items-center justify-center overflow-hidden"
                    style={{ borderColor: formData.primary_color }}
                  >
                    {formData.profile_image ? (
                      <img src={getImageUrl(formData.profile_image)} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-12 h-12 text-gray-500" />
                    )}
                  </div>
                  
                  <h3 className="text-2xl font-bold" style={{ color: formData.primary_color }}>
                    {formData.display_name || 'Your Name'}
                  </h3>
                  {formData.tagline && (
                    <p className="text-sm text-gray-400 mt-1">{formData.tagline}</p>
                  )}
                  {formData.bio && (
                    <p className="text-sm text-gray-300 mt-3">{formData.bio}</p>
                  )}

                  {/* Mobile Number Display */}
                  {formData.show_mobile && formData.mobile_number && (
                    <a 
                      href={`tel:${formData.mobile_number.replace(/\s/g, '')}`}
                      className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-full text-sm font-medium transition-transform hover:scale-105"
                      style={{ backgroundColor: `${formData.primary_color}20`, color: formData.primary_color }}
                    >
                      <Phone className="w-4 h-4" />
                      {formData.mobile_number}
                    </a>
                  )}

                  {/* Social Links */}
                  {formData.social_links.length > 0 && (
                    <div className="flex justify-center gap-3 mt-4 flex-wrap">
                      {formData.social_links.map((link, idx) => {
                        const Icon = getSocialIcon(link.platform);
                        return (
                          <a
                            key={idx}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-10 h-10 rounded-full flex items-center justify-center transition-transform hover:scale-110"
                            style={{ backgroundColor: `${getSocialColor(link.platform)}20` }}
                          >
                            <Icon className="w-5 h-5" style={{ color: getSocialColor(link.platform) }} />
                          </a>
                        );
                      })}
                    </div>
                  )}

                  {/* Custom Links */}
                  {formData.custom_links.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {formData.custom_links.map((link, idx) => (
                        <a
                          key={idx}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block px-4 py-3 rounded-xl text-center font-medium transition-transform hover:scale-[1.02]"
                          style={{ 
                            backgroundColor: `${formData.primary_color}20`,
                            color: formData.primary_color
                          }}
                        >
                          {link.name}
                        </a>
                      ))}
                    </div>
                  )}

                  {/* Referral Links Section */}
                  <div className="mt-6 pt-4 border-t border-white/10 space-y-3">
                    <p className="text-xs text-gray-500 font-medium">Join My Team</p>
                    
                    {/* Referral Code */}
                    <div className="text-center">
                      <p className="text-[10px] text-gray-500">Referral Code</p>
                      <p className="font-mono font-bold text-lg" style={{ color: formData.primary_color }}>
                        {user?.username || 'YOUR-CODE'}
                      </p>
                    </div>
                    
                    {/* Referral Link */}
                    <a
                      href={`${window.location.origin}/register?ref=${user?.username}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block px-3 py-2 rounded-lg text-xs font-medium text-center truncate"
                      style={{ backgroundColor: `${formData.primary_color}20`, color: formData.primary_color }}
                    >
                      Register Link
                    </a>
                  </div>
                </div>
              </div>
              );
            })()}

            {card && (
              <div className="mt-4 p-4 bg-white/5 rounded-xl">
                <p className="text-sm text-muted-foreground mb-3 text-center">Share your referral links:</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground w-20">Username:</span>
                    <code className="flex-1 text-xs bg-black/30 px-3 py-2 rounded-lg overflow-x-auto">
                      {`${window.location.origin}/register?ref=${user?.username}`}
                    </code>
                    <button 
                      onClick={async () => {
                        const success = await copyToClipboard(`${window.location.origin}/register?ref=${user?.username}`);
                        if (success) toast.success('Link 1 copied!');
                        else toast.error('Failed to copy');
                      }}
                      className="p-2 bg-primary/20 rounded-lg hover:bg-primary/30"
                    >
                      <Copy className="w-4 h-4 text-primary" />
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground w-20">Referral ID:</span>
                    <code className="flex-1 text-xs bg-black/30 px-3 py-2 rounded-lg overflow-x-auto">
                      {`${window.location.origin}/register?ref=${user?.id?.slice(0, 8) || user?.username}`}
                    </code>
                    <button 
                      onClick={async () => {
                        const success = await copyToClipboard(`${window.location.origin}/register?ref=${user?.id?.slice(0, 8) || user?.username}`);
                        if (success) toast.success('Link 2 copied!');
                        else toast.error('Failed to copy');
                      }}
                      className="p-2 bg-primary/20 rounded-lg hover:bg-primary/30"
                    >
                      <Copy className="w-4 h-4 text-primary" />
                    </button>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-3 text-center">Card Link:</p>
                <code className="text-xs bg-black/30 px-3 py-2 rounded-lg block overflow-x-auto text-center mt-1">
                  {cardUrl}
                </code>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DigitalCard;
